import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { AutomotiveAftermarketProfileItemBriefService } from './automotive-aftermarket-profile-item-brief.service';
import { AutomotiveAftermarketProfileDetails, AutomotiveAftermarketProfileModel } from '../models';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import {
  AutomotiveAftermarketProfileItemBriefClone,
  AutomotiveAftermarketProfileItemBriefDelete,
  AutomotiveAftermarketProfileItemBriefInitialState,
  AutomotiveAftermarketProfileItemBriefLoad,
  AutomotiveAftermarketProfileItemBriefSave,
} from './automotive-aftermarket-profile-item-brief.actions';
import { AutomotiveAftermarketProfileItemService } from '../item/automotive-aftermarket-profile-item.service';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface AutomotiveAftermarketProfileItemBriefStateModel extends AutomotiveAftermarketProfileDetails {
  loaded: boolean;
}

const initialState: AutomotiveAftermarketProfileItemBriefStateModel = {
  ...new AutomotiveAftermarketProfileDetails(),
  loaded: false,
};

@State<AutomotiveAftermarketProfileItemBriefStateModel>({
  name: 'automotiveAftermarketProfileItemBrief',
  defaults: initialState,
})
@Injectable()
export class AutomotiveAftermarketProfileItemBriefState {
  constructor(
    private automotiveAftermarketProfileItemBriefService: AutomotiveAftermarketProfileItemBriefService,
    private automotiveAftermarketProfileItemService: AutomotiveAftermarketProfileItemService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getProfileId(state: AutomotiveAftermarketProfileItemBriefStateModel): number {
    return state.id;
  }

  @Selector()
  static getState(
    state: AutomotiveAftermarketProfileItemBriefStateModel,
  ): AutomotiveAftermarketProfileItemBriefStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: AutomotiveAftermarketProfileItemBriefStateModel): boolean {
    return state.loaded;
  }

  @Action(AutomotiveAftermarketProfileItemBriefLoad)
  getAutomotiveAftermarketProfileItemBrief(
    ctx: StateContext<AutomotiveAftermarketProfileItemBriefStateModel>,
    { payload }: AutomotiveAftermarketProfileItemBriefLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.automotiveAftermarketProfileItemService.getAutomotiveAftermarketProfile(payload).pipe(
      map((automotiveAftermarketProfile: AutomotiveAftermarketProfileModel) => {
        ctx.patchState({ ...automotiveAftermarketProfile, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AutomotiveAftermarketProfileItemBriefSave)
  saveAutomotiveAftermarketProfileItemBrief(
    ctx: StateContext<AutomotiveAftermarketProfileItemBriefStateModel>,
    { payload }: AutomotiveAftermarketProfileItemBriefSave,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.automotiveAftermarketProfileItemBriefService.saveAutomotiveAftermarketProfile(payload).pipe(
      map((updatedProfile: AutomotiveAftermarketProfileModel) => {
        ctx.patchState({ ...updatedProfile });
        this.notificationService.success('api.automotiveAftermarketProfile.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AutomotiveAftermarketProfileItemBriefClone)
  cloneAutomotiveAftermarketProfileItemBrief(
    ctx: StateContext<AutomotiveAftermarketProfileItemBriefStateModel>,
    { payload }: AutomotiveAftermarketProfileItemBriefClone,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.automotiveAftermarketProfileItemBriefService.cloneAutomotiveAftermarketProfile(payload).pipe(
      map(() => {
        this.notificationService.success('api.automotiveAftermarketProfile.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/monitoring/automotive-aftermarket-profiles');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AutomotiveAftermarketProfileItemBriefDelete)
  deleteAutomotiveAftermarketProfileItemBrief(
    ctx: StateContext<AutomotiveAftermarketProfileItemBriefStateModel>,
    { payload }: AutomotiveAftermarketProfileItemBriefDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.automotiveAftermarketProfileItemBriefService.deleteAutomotiveAftermarketProfile(payload.profileId).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.automotiveAftermarketProfile.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/monitoring/automotive-aftermarket-profiles');
        return ctx.dispatch(new AutomotiveAftermarketProfileItemBriefInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AutomotiveAftermarketProfileItemBriefInitialState)
  setInitialState(ctx: StateContext<AutomotiveAftermarketProfileItemBriefStateModel>): void {
    ctx.setState(initialState);
  }
}
